import { Component, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, ViewChild,  HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiServiceService } from '../../services/common/api-service.service';
import { SubscriptionService } from '../../services/subscription/subscription.service';
import Swal from 'sweetalert2';
import { Location } from '@angular/common';

interface ProfileData {
  personal_info: any;
  basic_info: any;
  interest_status: string;
  _id: string;
  interest_owner_id: any;
  selected_profile_id: string;
}

interface SenderData {
  _id: string;
  interest_status: string;
  selected_profile_id: any;
}

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit, OnDestroy {
  @ViewChild('chatContainer') private chatContainer!: ElementRef;
  @ViewChild('messagesEnd') private messagesEnd!: ElementRef;

  newRequests: ProfileData[] = [];
  acceptedRequests: ProfileData[] = [];
  senderAcceptedRequests: SenderData[] = [];
  deniedRequests: ProfileData[] = [];

  uniqueChatList: any[] = [];
  conversations: any = null;
  isConversationEmpty: boolean = false;
  isChatRestricted: boolean = false;
  inputText: string = '';

  isEnable: boolean = false;
  currentReceiverId: string = '';
  currentSenderId: string = '';

  profiles: { [id: string]: { first_name: string; last_name: string; verify?: any } } = {};
  base64Images: { [oryId: string]: string } = {};
  conversationsMap: { [receiverId: string]: any } = {};
  loading = true;
  isMobile: boolean = false;
  isTyping: boolean = false;
  private chatPollInterval: any;
  presenceMap: { [profileId: string]: Date } = {};

  constructor(
    private apiService: ApiServiceService,
    private subscriptionService: SubscriptionService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private location: Location
  ) {
    this.currentSenderId = localStorage.getItem('profileId') || '';
  }

  ngOnInit() {
    this.checkScreenSize();
    this.loading = true;
    this.getSentConnectionListData();
    this.getReceivedConnectionListData();

    this.route.queryParams.subscribe((params) => {
      const receiverId = params['receiverId'];
      if (receiverId) {
        setTimeout(() => {
          this.selectConversation(receiverId);
        }, 800);
      }
    });

    setTimeout(() => {
      this.loading = false;
    }, 1200);

    // Polling to keep active conversation and read receipts in sync
    this.chatPollInterval = setInterval(() => {
      if (this.currentReceiverId && !this.isChatRestricted) {
        this.getConversation(this.currentReceiverId, true);
      }
      
      // Update presence for unique chat list
      const profileIds = this.uniqueChatList.map(c => c._id);
      if (profileIds.length > 0) {
        this.apiService.getPresence(profileIds).subscribe(
          (response) => {
            if (response.status === 'success' && response.data) {
              response.data.forEach((p: any) => {
                if (p.last_seen) {
                  const lastSeenDate = new Date(p.last_seen);
                  this.presenceMap[p._id] = lastSeenDate;
                  const isOnline = (Date.now() - lastSeenDate.getTime()) < 120000;
                  console.log(`[Presence Poll] Profile: ${p._id}, last_seen: ${p.last_seen}, computed online: ${isOnline}`);
                } else {
                  console.log(`[Presence Poll] Profile: ${p._id}, last_seen: undefined/null`);
                }
              });
              this.cdr.detectChanges();
            }
          },
          (err) => console.error('Error fetching presence:', err)
        );
      }
    }, 5000);
  }
  goBack(): void {
  this.location.back();
}
  isOnline(profileId: string): boolean {
    const lastSeen = this.presenceMap[profileId];
    if (!lastSeen) return false;
    // Online if last seen within 2 minutes (120,000 ms)
    return (Date.now() - lastSeen.getTime()) < 120000;
  }

  timeAgo(profileId: string): string {
    const date = this.presenceMap[profileId];
    if (!date) return 'Unknown';
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min${minutes !== 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
  }

  ngOnDestroy() {
    if (this.chatPollInterval) {
      clearInterval(this.chatPollInterval);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    this.isMobile = window.innerWidth < 768;
  }

  // ngAfterViewChecked() {
  //   this.scrollToBottom();
  // }

  private scrollToBottom(): void {
    try {
      if (this.chatContainer) {
        const el = this.chatContainer.nativeElement;

        el.scrollTo({
          top: el.scrollHeight,
          behavior: 'smooth',
        });
      }
    } catch (err) {}
  }

  getAvatarKey(profile: any): string {
    return (
      profile?.basic_info?.keycloak_id ||
      profile?.basic_info?.ory_identity_id ||
      ''
    );
  }

  loadImage(imagePath: string, profileId: string): void {
    if (this.base64Images[profileId]) return;
    this.apiService.getImageBase64(imagePath).subscribe(
      (data) => {
        if (data && data.base64Image) {
          this.base64Images[profileId] = data.base64Image;
        }
      },
      (error) => console.error('Error fetching image base64:', error),
    );
  }

  updateUniqueChatList() {
    const chatMap = new Map<string, any>();

    this.acceptedRequests.forEach((request) => {
      const otherId = request.interest_owner_id?._id;
      if (otherId && !chatMap.has(otherId)) {
        chatMap.set(otherId, request.interest_owner_id);
      }
    });

    this.senderAcceptedRequests.forEach((request) => {
      const otherId = request.selected_profile_id?._id;
      if (otherId && !chatMap.has(otherId)) {
        chatMap.set(otherId, request.selected_profile_id);
      }
    });

    this.uniqueChatList = Array.from(chatMap.values());
  }

  get sortedUniqueChatList() {
    return this.uniqueChatList.sort((a, b) => {
      const convA = this.conversationsMap[a._id];
      const convB = this.conversationsMap[b._id];
      const timeA = convA && convA.messages && convA.messages.length > 0 
        ? new Date(convA.messages[convA.messages.length - 1].timestamp).getTime() 
        : Number.MIN_SAFE_INTEGER;
      const timeB = convB && convB.messages && convB.messages.length > 0 
        ? new Date(convB.messages[convB.messages.length - 1].timestamp).getTime() 
        : Number.MIN_SAFE_INTEGER;
      
      return timeB - timeA;
    });
  }

  fetchConversationForListItem(receiverId: string): void {
    const senderId = this.currentSenderId;
    if (senderId && receiverId) {
      this.apiService.getConversation(senderId, receiverId).subscribe(
        (response) => {
          if (
            response.status === 'success' &&
            response.data &&
            response.data.length > 0
          ) {
            this.conversationsMap[receiverId] = response.data[0];
          } else {
            this.conversationsMap[receiverId] = null;
          }
          this.cdr.detectChanges();
        },
        (error) => {
          this.conversationsMap[receiverId] = null;
          this.cdr.detectChanges();
        },
      );
    }
  }

  getUnreadCount(receiverId: string): number {
    const conv = this.conversationsMap[receiverId];
    if (!conv || !conv.messages) return 0;
    return conv.messages.filter(
      (m: any) =>
        m.sender_id !== this.currentSenderId && m.message_subject === 'Pending',
    ).length;
  }

  getLastMessage(receiverId: string): string {
    const conv = this.conversationsMap[receiverId];
    if (!conv || !conv.messages || conv.messages.length === 0) {
      return 'No conversation started yet';
    }
    const lastMsg = conv.messages[conv.messages.length - 1];
    if (lastMsg.sender_id === this.currentSenderId) {
      return 'You: ' + lastMsg.content;
    } else {
      return lastMsg.content;
    }
  }

  getLastMessageTime(receiverId: string): any {
    const conv = this.conversationsMap[receiverId];
    if (!conv || !conv.messages || conv.messages.length === 0) return null;
    const date = new Date(conv.messages[conv.messages.length - 1].timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  selectConversation(receiverId: string) {
    this.isEnable = true;
    if (this.currentReceiverId === receiverId) {
      // Do nothing, already selected
    } else {
      this.currentReceiverId = receiverId;
      this.isChatRestricted = false;

      const partner = this.uniqueChatList.find(p => p._id === receiverId);
      const myPlanCanChat = this.subscriptionService.hasFeature('chatAccess');
      const partnerPlan = (partner?.current_plan || partner?.plan || 'FREE').toUpperCase();
      if (!myPlanCanChat && partnerPlan === 'FREE') {
        this.isChatRestricted = true;
      }

      this.getConversation(receiverId);
    }
  }

  fetchProfile(profileId: string): void {
    if (this.profiles[profileId]) return;
    const sessionToken = localStorage.getItem('sessionToken') || '';
    this.apiService.getEditDetails(sessionToken, profileId).subscribe(
      (response) => {
        if (response?.data?.basic_info) {
          this.profiles[profileId] = {
            first_name: response.data.basic_info.first_name,
            last_name: response.data.basic_info.last_name,
            verify: response.data.verify,
          };
        }
      },
      (error) => console.error('Error fetching profile detail:', error),
    );
  }

  getConversation(receiverId: string, isPolling = false): void {
    const senderId = this.currentSenderId;
    if (senderId && receiverId) {
      this.apiService.getConversation(senderId, receiverId).subscribe(
        (response) => {
          this.isChatRestricted = false;
          this.conversations = response.data || [];
          this.isConversationEmpty = this.conversations.length === 0;
          this.fetchProfile(senderId);
          this.fetchProfile(receiverId);

          if (this.conversations && this.conversations.length > 0) {
            const activeConv = this.conversations[0];
            let updated = false;
            activeConv.messages.forEach((m: any) => {
              if (m.sender_id !== senderId && m.message_subject === 'Pending') {
                m.message_subject = 'Read';
                updated = true;
              }
            });
            this.conversationsMap[receiverId] = activeConv;

            if (updated) {
              this.apiService.markMessagesRead(senderId, receiverId).subscribe({
                next: () => {},
                error: (err) =>
                  console.error('Error marking messages as read:', err),
              });
            }
          }
          if (!isPolling) {
            this.cdr.detectChanges();
            setTimeout(() => this.scrollToBottom(), 100);
          } else {
            this.cdr.detectChanges();
          }
        },
        (error) => {
          if (error?.status === 403 || error?.error?.code === 'UPGRADE_REQUIRED' || error?.error?.code === 'LIMIT_REACHED') {
            this.isChatRestricted = true;
            this.isConversationEmpty = true;
            this.conversations = [];
            return;
          }
          if (this.apiService.handleQuotaError(error)) return;
          if (error.status === 404) {
            this.isConversationEmpty = true;
            this.conversations = [];
          }
        },
      );
    }
  }

  handleSend(e?: Event) {
    if (e) e.preventDefault();
    if (!this.currentReceiverId || this.inputText.trim() === '' || this.isChatRestricted) return;

    const senderId = this.currentSenderId;
    if (!senderId) return;

    const textVal = this.inputText.trim();
    const newMessage = {
      sender_id: senderId,
      content: textVal,
      timestamp: new Date(),
      message_subject: 'Sending...'
    };

    if (this.conversations && this.conversations.length > 0) {
      this.conversations[0].messages.push(newMessage);
      this.conversationsMap[this.currentReceiverId] = this.conversations[0];
    } else {
      const newConv = { messages: [newMessage] };
      this.conversations = [newConv];
      this.conversationsMap[this.currentReceiverId] = newConv;
      this.isConversationEmpty = false;
    }

    this.cdr.detectChanges();
    this.scrollToBottom();

    this.inputText = '';

    this.apiService
      .sendMessage(senderId, this.currentReceiverId, textVal)
      .subscribe(
        (response) => {
          if (response.status !== 'success') {
            console.error('Error sending message:', response.data);
            this.removeFailedMessage(newMessage);
            this.inputText = textVal;
          } else {
            if (response.data && response.data.messages) {
              this.conversations = [response.data];
              this.conversationsMap[this.currentReceiverId] = response.data;
              this.cdr.detectChanges();
            }
          }
        },
        (error) => {
          this.removeFailedMessage(newMessage);
          this.inputText = textVal;
          if (error?.status === 403 || error?.error?.code === 'UPGRADE_REQUIRED' || error?.error?.code === 'LIMIT_REACHED') {
            this.isChatRestricted = true;
            return;
          }
          if (this.apiService.handleQuotaError(error)) return;
          console.error('Error sending message API:', error);
        },
      );
  }

  removeFailedMessage(msgRef: any) {
    if (this.conversations && this.conversations.length > 0) {
      const idx = this.conversations[0].messages.indexOf(msgRef);
      if (idx !== -1) {
        this.conversations[0].messages.splice(idx, 1);
      }
      if (this.conversations[0].messages.length === 0) {
        this.isConversationEmpty = true;
      }
      this.conversationsMap[this.currentReceiverId] = this.conversations[0];
      this.cdr.detectChanges();
    }
  }

  openUpgradeModal() {
    this.router.navigate(['/plans']);
  }

  applySuggestText(type: string) {
    if (type === 'jaipur') {
      this.inputText = 'Tell me more about your Jaipur trip!';
    } else if (type === 'values') {
      this.inputText = 'What are your traditional family values like?';
    } else if (type === 'zoom') {
      this.inputText = "Let's connect on Zoom this Saturday!";
    }
  }

  handleSimulateCall() {
    Swal.fire({
      title: 'Outgoing Secure Call',
      html: `Connecting encrypted line to <b class="text-primary">${this.getActivePartnerName()}</b>...`,
      icon: 'info',
      confirmButtonText: 'End Call',
      confirmButtonColor: '#8c002a',
      showCancelButton: true,
      cancelButtonText: 'Mute My Mic',
    });
  }

  handleShareHoroscope() {
    Swal.fire({
      title: 'Share Horoscope Chart?',
      text: `Would you like to securely authorize ${this.getActivePartnerName()} to review your astrological birth placements?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Authorize & Share',
      confirmButtonColor: '#8c002a',
      cancelButtonColor: '#635d5e',
    }).then((result) => {
      if (result.isConfirmed) {
        this.inputText = '📎 Shared Astrological Horoscope Chart';
        this.handleSend();
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Horoscope link transferred',
          showConfirmButton: false,
          timer: 2000,
        });
      }
    });
  }

  getReceivedConnectionListData(): void {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileId = this.currentSenderId;
    if (!sessionToken || !profileId) return;

    this.apiService
      .getReceivedConnectionList(sessionToken, profileId)
      .subscribe(
        (response: { data: ProfileData[] }) => {
          const received = response.data || [];
          this.newRequests = received.filter(
            (request: ProfileData) => request.interest_status === 'Pending',
          );
          this.deniedRequests = received.filter(
            (request: ProfileData) => request.interest_status === 'Denied',
          );
          this.acceptedRequests = received.filter(
            (request: ProfileData) => request.interest_status === 'Accepted',
          );

          this.acceptedRequests.forEach((request) => {
            const owner = request.interest_owner_id;
            if (owner) {
              const keycloakId =
                owner.basic_info?.keycloak_id ||
                owner.basic_info?.ory_identity_id ||
                owner._id;
              const avatarId = owner.personal_info?.avatar_Id;
              if (keycloakId && avatarId) {
                const imagePath = `${keycloakId}/gallery/avatar/${avatarId}`;
                this.loadImage(imagePath, owner._id);
              }
              this.fetchConversationForListItem(owner._id);
            }
          });
          this.updateUniqueChatList();
        },
        (error) => console.error('Error fetching received connections:', error),
      );
  }

  getSentConnectionListData() {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileId = this.currentSenderId;
    if (!sessionToken || !profileId) return;

    this.apiService.getSentConnectionList(sessionToken, profileId).subscribe(
      (response: { data: SenderData[] }) => {
        const sent = response.data || [];
        this.senderAcceptedRequests = sent.filter(
          (request: SenderData) => request.interest_status === 'Accepted',
        );

        this.senderAcceptedRequests.forEach((request) => {
          const selected = request.selected_profile_id;
          if (selected) {
            const keycloakId =
              selected.basic_info?.keycloak_id ||
              selected.basic_info?.ory_identity_id ||
              selected._id;
            const avatarId = selected.personal_info?.avatar_Id;
            if (keycloakId && avatarId) {
              const imagePath = `${keycloakId}/gallery/avatar/${avatarId}`;
              this.loadImage(imagePath, selected._id);
            }
            this.fetchConversationForListItem(selected._id);
          }
        });
        this.updateUniqueChatList();
      },
      (error) => console.error('Error fetching sent connections:', error),
    );
  }

  getActivePartnerName(): string {
    const profile = this.profiles[this.currentReceiverId];
    if (profile) return `${profile.first_name} ${profile.last_name}`;

    const chatUser = this.uniqueChatList.find(
      (u) => u._id === this.currentReceiverId,
    );
    if (chatUser)
      return `${chatUser.basic_info.first_name} ${chatUser.basic_info.last_name}`;

    return 'Chat Room';
  }

  formatTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  isPartnerVerified(): boolean {
    if (!this.currentReceiverId) return false;
    const profile = this.profiles[this.currentReceiverId];
    if (profile) return (profile as any).verification_status === 'VERIFIED';

    const chatUser = this.uniqueChatList.find(
      (u: any) => u._id === this.currentReceiverId,
    );
    if (chatUser) return chatUser.verification_status === 'VERIFIED';

    return false;
  }
  backToInbox(): void {
  this.currentReceiverId = '';
  this.isEnable = false;
  this.isChatRestricted = false;
  this.inputText = '';
  this.isTyping = false;

  this.cdr.detectChanges();
}
}
